import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { Field, Formik } from 'formik';
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  Animated,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { TaskInviteRequestButton } from '@components/Invite/TaskInviteRequestButton';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { NetInfoModal } from '@components/Modals/NetInfoModal.web';
import { ChecklistCommonModal } from '@components/ProjectDetail/ProjectChecklist/ChecklistCommonModal.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { MenuItem } from '@components/shared/Popup/PopupItem';
import {
  ChecklistRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import { TextFieldMultiline } from '@components/shared/TextField/TextFieldMultiline.web';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { FileDeletedToast } from '@components/TaskList/FileDeletedToast.web';
import { FileShareLink } from '@components/TaskList/FileShareLink.web';
import { MyTasksDueDateRangePicker } from '@components/TaskList/MyTasksDueDateRangePicker.web';
import { PrioritySelector } from '@components/TaskList/PrioritySelector.web';
import { TaskAssigneeMembersButtonV3 } from '@components/TaskList/TaskAssigneeMembersButtonV3.web';
import {
  UserSearchType,
  StringSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskShareLink } from '@components/TaskList/TaskShareLink.web';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import { TaskJoinedGuidanceCard } from '@components/Tasks/TaskJoinedGuidanceCard.web';
import { TaskRequestSentGuidanceCard } from '@components/Tasks/TaskRequestSentGuidanceCard.web';
import ToggleSwitch from '@components/Tasks/ToggleSwitch.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupIcon } from '@components/Web/PopupIcon';
import {
  Project,
  Task,
  TaskStatus,
  useDeleteTaskMutation,
  User,
  useUpdateTaskMutation,
  TaskPriority,
  TaskMemberRole,
  TaskChecklist,
  useAddTaskChecklistMutation,
  useRemoveTaskChecklistMutation,
  useUpdateTaskChecklistMutation,
  useListTaskChecklistQuery,
  ListProjectsDocument,
  useAddTaskFilesMutation,
  GetTaskDocument,
  LocalFile,
  DocumentAttributes,
  Document,
  TeamMemberRole,
  ProjectMemberRole,
  useUpdateTaskChecklistSortMutation,
} from '@graphql/generated';
import { useDeleteTaskFiles } from '@hooks/useDeleteTaskFile';
import useDownloadFiles from '@hooks/useDownloadFiles';
import { useEnsureCanCompleteTask } from '@hooks/useEnsureCanCompleteTask';
import useFileProcessor from '@hooks/useFileProcessor';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import useMe from '@hooks/useMe';
import useTask from '@hooks/useTask';
import { useTaskDetailMenuActions } from '@hooks/useTaskDetailMenuActions';
import { useTaskMembership } from '@hooks/useTaskMembership';
import { useToggleCompleteTask } from '@hooks/useToggleCompleteTask';
import { useUploadFiles } from '@hooks/useUploadFiles';
import { DndKitSortableChecklistListWeb } from '@navigation/tasks/DndKitSortableChecklistList.web';
import { TaskChecklistItem } from '@navigation/tasks/TaskChecklistItem';
import { TaskPanelDragOverlay } from '@navigation/tasks/TaskPanelDragOverlay.web';
import { editTaskDescriptionSchema } from '@schemas/editTaskDescriptionSchema';
import { editTaskNameSchema } from '@schemas/editTaskNameSchema';
import { TaskActivityLog } from '@screens/Tasks/TaskActivityLog';
import TaskFilesAndMedia from '@screens/Tasks/TaskFilesAndMedia';
import { TaskMembersNew } from '@screens/Tasks/TaskMembersNew.web';
import {
  TASK_NAME_LENGTH_LIMIT,
  TASK_DESC_LENGTH_LIMIT,
  MIXPANEL_EVENTS,
} from '@src/constants/constants';
import { useNetInfo } from '@src/context/NetInfoContext';
import { useMixpanel } from '@src/utils/mixpanel';
import theme from '@themes/theme';
import { useFilePicker } from '@utils/filePicker';
import { fileToLocalFile } from '@utils/fileUtils';
import { localDateFromUTCDate } from '@utils/formatters/date';
import { compressImages } from '@utils/imageCompression';
import { AsyncStorage } from '@utils/storage';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

import type { UploadProgressData } from 'expo-file-system/legacy';

export function getUploadProgressSummary(files: { progress: number }[]) {
  const total = files.length;
  const completed = files.filter((f) => f.progress === 1).length;

  return {
    completed,
    total,
    label: `${completed} of ${total} file${total !== 1 ? 's' : ''}`,
  };
}

export enum FilterType {
  NoFilter = '',
  MyUploads = 'MyUploads',
}

export type ProjectDetailTabsParamList = {
  'task-details': undefined;
  'activity-log': { taskId: Task['id'] };
  members: { taskId: Task['id']; isTaskAssignee?: boolean };
  files: { taskId: Task['id'] };
  checklist: { taskId: Task['id'] };
};

// The unified form value type for a single Formik instance
export type FormValues = {
  name: string;
  description: string;
  fixedOrder: boolean;
  frequency?: string | null;
  dependencyIds: string[];
  recurring: boolean;
  projectId: string;
  phaseId?: string;
  priority: TaskPriority;
  status: TaskStatus;
  members: {
    userId: string;
    role: TaskMemberRole;
  }[];
  skillIds: string[];
  startDate?: Date;
  dueDate?: Date;
  completedAt: Task['completedAt'] | null;
};

export const TaskDetailTabs = ({
  // isMaximize,
  // isFromMyTask,
  task,
  onClose,
  // onMaximize,
  externalNavigate,
  // onPressCreator,
  // onPressMember,
  onPressProject,
  onNavigateToAccount,
  onNavigateToProjects,
}: {
  isMaximize: boolean;
  isFromMyTask: boolean;
  task: Task;
  onClose: () => void;
  onMaximize: () => void;
  externalNavigate?: any;
  onPressCreator?: (task: Task) => void;
  onPressMember?: (item: User) => void;
  onPressProject?: (project: Project) => void;
  onNavigateToAccount?: () => void;
  onNavigateToProjects?: () => void;
}) => {
  const { t } = useTranslation('models');
  const toast = useToast();
  const { trackEvent } = useMixpanel();

  const { completedAt, status } = task;

  const { navItems: rawNavItems, paywallModalNode } =
    useTaskDetailMenuActions(task);
  const navItems = rawNavItems?.filter((i): i is MenuItem => 'name' in i);
  // TA-10619: hiding the not-yet-shipped web items can leave a role (a task
  // Viewer) with nothing actionable, so don't render a trigger that opens an
  // empty or all-disabled menu. Mirrors PopupItem's own disabled logic
  // (`isComingSoon || disable`), same as ProjectDetailModal.web.
  const hasExecutableTaskOption = navItems?.some(
    (item) => !item.isComingSoon && !item.disable
  );
  const { canViewTask, canEditTaskSecondLevel } = useTaskMembership(task);

  const navigation = useNavigation();
  const didApplyInitialTab = useRef(false);

  const [openAssigneeModal, setOpenAssigneeModal] = useState(false);

  useEffect(() => {
    // this part is to open checklist or assignee modal when opening task panel.
    const applyInitialTabFromStorage = async () => {
      // Ensure this logic only runs once
      if (didApplyInitialTab.current) return;

      const openChecklist =
        (await AsyncStorage.getItem('TaskWebPanelOpenChecklist')) === '1';
      const openAssignee =
        (await AsyncStorage.getItem('TaskWebPanelOpenAssignee')) === '1';

      if (openChecklist && canViewTask) {
        // Mark as applied before navigating to avoid race conditions
        didApplyInitialTab.current = true;

        // Navigate to checklist tab
        navigation.navigate('checklist' as never);

        // Clean up the flag so it won't affect next open
        await AsyncStorage.deleteItem('TaskWebPanelOpenChecklist');
      } else if (openAssignee && canViewTask) {
        didApplyInitialTab.current = true;
        setOpenAssigneeModal(true);
        await AsyncStorage.deleteItem('TaskWebPanelOpenAssignee');
      } else {
        didApplyInitialTab.current = true;
      }
    };

    applyInitialTabFromStorage();
  }, [canViewTask, navigation]);

  const completed = status === TaskStatus.Completed || !!completedAt;
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState<boolean>();

  // checklist

  const { data: checklistData, loading: checklistLoading } =
    useListTaskChecklistQuery({
      variables: {
        id: task.id,
      },
    });

  const { listTaskChecklist } = checklistData || {
    listTaskChecklist: undefined,
  };

  const [showChecklistCompletedItems, setShowChecklistCompletedItems] =
    useState(false);

  const [taskChecklist, setTaskChecklist] = useState<TaskChecklist[]>([]);

  useEffect(() => {
    listTaskChecklist && setTaskChecklist(listTaskChecklist);
  }, [listTaskChecklist]);

  const [addTaskChecklist] = useAddTaskChecklistMutation({
    update(cache, { data }) {
      if (!data?.addTaskChecklist) return;

      cache.modify({
        id: cache.identify({ __typename: 'Task', id: task.id }),
        fields: {
          updatedAt() {
            return new Date().toISOString();
          },
          checklists() {
            return [...data.addTaskChecklist];
          },
        },
      });
    },
    refetchQueries: ['listTaskChecklist'],
  });

  const [removeTaskChecklist] = useRemoveTaskChecklistMutation({
    update(cache, { data }, { variables }) {
      if (!data?.removeTaskChecklist) return;

      const removedChecklistId = variables?.id;
      if (!removedChecklistId) return;

      cache.modify({
        id: cache.identify({ __typename: 'Task', id: task.id }),
        fields: {
          checklists(existingRefs = [], { readField }) {
            return existingRefs.filter(
              (ref) => readField('id', ref) !== removedChecklistId
            );
          },
        },
      });

      cache.evict({
        id: cache.identify({
          __typename: 'TaskChecklist',
          id: removedChecklistId,
        }),
      });

      cache.gc();
    },
    refetchQueries: ['listTaskChecklist'],
  });

  const [updateTaskChecklist, { data: updateTaskChecklistData }] =
    useUpdateTaskChecklistMutation({
      refetchQueries: ['listTaskChecklist'],
    });

  useEffect(() => {
    if (!updateTaskChecklistData) return;
    const { updateTaskChecklist: updatedItem } = updateTaskChecklistData;
    if (updatedItem) {
      trackEvent(MIXPANEL_EVENTS.CHECKLIST_ITEM_TOGGLED, {
        task_id: task.id,
        checklist_item_id: updatedItem.id,
        completed: !!updatedItem.completedAt,
      });
    }
  }, [updateTaskChecklistData]);

  const onCompletedTaskChecklistItem = async (
    item: TaskChecklist,
    callback?: () => void
  ) => {
    await updateTaskChecklist({
      variables: {
        id: item.id,
        attributes: {
          name: item.name,
          completed: !item.completedAt,
          sort: item.sort,
        },
      },
      onCompleted: () => {
        callback?.();
      },
      onError: (error) => {
        // TA-10511: previously only called the callback with no user-facing
        // feedback, so a rejected update (e.g. a task assignee without
        // project access) looked like clicking the checkbox did nothing.
        toast.show(error.message || t('tasks.checklist.updateFailed'), {
          placement: 'bottom',
          duration: 3000,
        });
        callback?.();
      },
    });
  };

  const [updateTaskChecklistSort] = useUpdateTaskChecklistSortMutation();
  const [processing, setProcessing] = useState(false);

  const onDragEnd = (data: TaskChecklist[]) => {
    setProcessing(true);
    const sortlist = data.map((item: TaskChecklist, index: number) => {
      return { ...item, sort: index + 1 };
    });
    setTaskChecklist(sortlist);
    const list = data.map((item: TaskChecklist, index: number) => {
      return {
        id: item.id,
        name: item.name,
        completed: item.completedAt !== null,
        sort: index + 1,
      };
    });
    updateTaskChecklistSort({
      variables: {
        taskId: task.id,
        attributes: list,
      },
      refetchQueries: ['listTaskChecklist'],
      onCompleted: () => {
        setProcessing(false);
      },
      onError: () => {
        setProcessing(false);
      },
    });
  };

  const onSaveTaskChecklistItem = async (
    name: string,
    item?: TaskChecklist
  ) => {
    if (item) {
      await updateTaskChecklist({
        variables: {
          id: item.id,
          attributes: {
            name: name,
            completed: item.completedAt !== null,
            sort: item.sort,
          },
        },
      });
    } else {
      await addTaskChecklist({
        variables: {
          taskId: task.id,
          attributes: {
            name: name,
          },
        },
      });
    }
  };

  const onDeleteTaskChecklistItem = async (itemId: string) => {
    await removeTaskChecklist({
      variables: {
        id: itemId,
      },
    });
  };

  // =============================
  // Checklist collapse / expand animation
  // =============================
  const [isChecklistOpen, setIsChecklistOpen] = useState(true);

  // 1 = expanded, 0 = collapsed
  const checklistAnim = useRef(new Animated.Value(1)).current;
  // Store measured content height for smooth height animation
  const [checklistContentHeight, setChecklistContentHeight] = useState(0);
  const [checklistContentWidth, setChecklistContentWidth] = useState(0);

  // Height interpolation based on measured content height
  const checklistHeight = checklistAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, checklistContentHeight || 1],
  });

  // Rotate chevron from 0deg (down) to 180deg (up)
  const checklistRotate = checklistAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const toggleChecklistSection = () => {
    const next = !isChecklistOpen;
    setIsChecklistOpen(next);
    Animated.timing(checklistAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      useNativeDriver: false, // height cannot use native driver
    }).start();
  };

  // =============================
  // Files & Media collapse / expand animation
  // =============================
  const [isFilesAndMediaOpen, setIsFilesAndMediaOpen] = useState(false);
  const [isShowFilter, setIsShowFilter] = useState(false);

  // 1 = expanded, 0 = collapsed
  const filesAndMediaAnim = useRef(new Animated.Value(0)).current;
  // Store measured content height for smooth height animation
  const [filesAndMediaContentHeight, setFilesAndMediaContentHeight] =
    useState(0);

  // Height interpolation based on measured content height
  const filesAndMediaHeight = filesAndMediaAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, filesAndMediaContentHeight || 1],
  });

  // Rotate chevron from 0deg (down) to 180deg (up)
  const filesAndMediaRotate = filesAndMediaAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const toggleFilesAndMediaSection = () => {
    const next = !isFilesAndMediaOpen;
    setIsFilesAndMediaOpen(next);
    Animated.timing(filesAndMediaAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      useNativeDriver: false, // height cannot use native driver
    }).start();
  };

  // =============================
  // Members collapse / expand animation
  // =============================
  const [isMembersOpen, setIsMembersOpen] = useState(false);

  // 1 = expanded, 0 = collapsed
  const membersAnim = useRef(new Animated.Value(0)).current;
  // Store measured content height for smooth height animation
  const [membersContentHeight, setMembersContentHeight] = useState(0);

  // Height interpolation based on measured content height
  const membersHeight = membersAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, membersContentHeight || 1],
  });

  // Rotate chevron from 0deg (down) to 180deg (up)
  const membersRotate = membersAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const toggleMembersSection = () => {
    const next = !isMembersOpen;
    setIsMembersOpen(next);
    Animated.timing(membersAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      useNativeDriver: false, // height cannot use native driver
    }).start();
  };

  // =============================
  // Activity collapse / expand animation
  // =============================
  const [isActivityOpen, setIsActivityOpen] = useState(false);

  // 1 = expanded, 0 = collapsed
  const activityAnim = useRef(new Animated.Value(0)).current;
  // Store measured content height for smooth height animation
  const [activityContentHeight, setActivityContentHeight] = useState(0);

  // Height interpolation based on measured content height
  const activityHeight = activityAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, activityContentHeight || 1],
  });

  // Rotate chevron from 0deg (down) to 180deg (up)
  const activityRotate = activityAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const toggleActivitySection = () => {
    const next = !isActivityOpen;
    setIsActivityOpen(next);
    Animated.timing(activityAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      useNativeDriver: false, // height cannot use native driver
    }).start();
  };

  // Preload avatars for task members
  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();
  useEffect(() => {
    const avatarIds = new Set<string>();
    task?.members.forEach((p) => {
      if (p.user.avatar?.id) {
        avatarIds.add(p.user.avatar.id);
      }
    });

    checkUserAvatarURLByIds([...avatarIds]);
  }, [task, checkUserAvatarURLByIds]);

  const { toggleCompleteTask } = useToggleCompleteTask();
  // TA-11037: role may have changed since the page loaded — refresh first.
  const ensureCanCompleteTask = useEnsureCanCompleteTask();
  const { showDeleteTaskModal, setShowDeleteTaskModal } = useTask();
  const { setIsTaskWebPanelOpen, setPaywallModalOpen, setPaywallTeam } =
    useWebDrawer();

  // Editing states for inline title and description
  // const isEditingDes = useRef<boolean>(false);
  const inputRef = useRef<any>(null);
  const inputRefDes = useRef<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDes, setIsEditingDes] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveredDes, setIsHoveredDes] = useState(false);
  const [isHoveredClose, setIsHoveredClose] = useState(false);
  const [isHoveredCopyLinkToInvite, setIsHoveredCopyLinkToInvite] =
    useState(false);
  const [isHoveredCompleteTask, setIsHoveredCompleteTask] = useState(false);

  const [deleteTask, { data: deleteTaskData, loading: isDeletingTask }] =
    useDeleteTaskMutation({
      refetchQueries: ['listTasks', 'listActivitiesByProject'],
    });

  useEffect(() => {
    if (!deleteTaskData) return;
    setShowDeleteTaskModal(false);
    setIsTaskWebPanelOpen(false);
  }, [deleteTaskData]);

  // Build unified initial values for a single Formik form
  const initialValues: FormValues = useMemo(() => {
    const startDate = task.startDate && localDateFromUTCDate(task.startDate);
    const dueDate = task.dueDate && localDateFromUTCDate(task.dueDate);

    return {
      name: task.name ?? '',
      description: task.description ?? '',
      fixedOrder: task?.fixedOrder ?? false,
      frequency: task?.frequency ?? undefined,
      dependencyIds: task.dependencies?.map((d) => d.id) || [],
      recurring: task?.recurring ?? false,
      projectId: task?.projectId ?? '0',
      phaseId: task?.phaseId ?? undefined,
      priority: task?.priority ?? TaskPriority.Low,
      status: task?.status ?? TaskStatus.Ongoing,
      members: (task?.members ?? []).map((m) => ({
        userId: m.user.id,
        role: m.role,
      })),
      skillIds: task?.skillIds ?? [],
      startDate,
      dueDate,
      completedAt: task?.completedAt ?? null,
    };
  }, [task]);

  const [updateTask] = useUpdateTaskMutation({ refetchQueries: ['listTasks'] });

  // Local name used to restore title if update failed or canceled
  const [localTaskName, setLocalTaskName] = useState(task.name);
  const [localTaskDesc, setLocalTaskDesc] = useState(task.description);

  const textProjectNameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalTaskName(task.name);
  }, [task.name]);

  useEffect(() => {
    setLocalTaskDesc(task.description);
  }, [task.description]);

  // Submit only name field (called on blur of title input)
  const submitTaskName = (values: FormValues) => {
    if (!task?.id || !task?.projectId || !values.name) return;

    setLocalTaskName(values.name);

    const members = task.members.map((u) => ({
      userId: u.user.id,
      role: u.role,
    }));

    updateTask({
      variables: {
        id: task?.id,
        attributes: {
          name: values.name,
          projectId: task.projectId,
          members,
        },
      },
      onCompleted: () => {
        setIsEditing(false);
      },
      onError: (error) => {
        // Revert local name when update fails
        setLocalTaskName(task.name);
        console.error('Failed to update task name:', error);
      },
      refetchQueries: ['listTasks', 'listProjects'],
    });
  };

  // Submit only description field (called on blur of description input)
  const submitTaskDescription = (values: FormValues) => {
    if (!task?.id || !task?.projectId) return;

    const members = task.members.map((u) => ({
      userId: u.user.id,
      role: u.role,
    }));

    updateTask({
      variables: {
        id: task?.id,
        attributes: {
          name: task.name,
          description: values.description,
          priority: task.priority,
          projectId: task.projectId,
          members,
        },
      },
      onCompleted: () => {
        setIsEditingDes(false);
        setIsHoveredDes(false);
      },
      refetchQueries: [
        { query: ListProjectsDocument, variables: { includeRecent: false } },
      ],
    });
  };

  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: task.startDate ? dayjs(task.startDate) : undefined,
    end: task.dueDate ? dayjs(task.dueDate) : undefined,
  });

  // Update priority using current form values as base
  const doUpdateTask = useCallback(
    (priority: TaskPriority, values: FormValues) => {
      if (priority === task.priority) return;
      updateTask({
        variables: {
          id: task.id,
          attributes: {
            ...values,
            priority,
          },
        },
      });
    },
    [updateTask, task.id, task.priority]
  );

  const [selectedDateRangeStr, setSelectedDateRangeStr] = useState<string>('');

  useEffect(() => {
    const start = selectedDueDateRange?.start;
    const end = selectedDueDateRange?.end;

    if (start && end) {
      const startD = dayjs(start);
      const endD = dayjs(end);

      const sameYear = startD.year() === endD.year();
      const sameMonth = sameYear && startD.month() === endD.month();

      let label = '';

      if (sameMonth) {
        // Example: Oct 11 - 23, 2025
        const month = startD.format('MMM');
        const startDay = startD.format('D');
        const endDay = endD.format('D');
        const year = startD.format('YYYY');
        label =
          year === new Date().getFullYear().toString()
            ? `${month} ${startDay} - ${endDay}`
            : `${month} ${startDay} - ${endDay}, ${year}`;
      } else {
        // Example: Oct 11 - Dec 23, 2025
        const startYear = startD.format('YYYY');
        const endYear = endD.format('YYYY');
        label = `${startD.format(
          startYear === new Date().getFullYear().toString()
            ? 'MMM D'
            : 'MMM D, YYYY'
        )} - ${endD.format(
          endYear === new Date().getFullYear().toString()
            ? 'MMM D'
            : 'MMM D, YYYY'
        )}`;
      }

      setSelectedDateRangeStr(label);
    } else if (start) {
      const startD = dayjs(start);
      setSelectedDateRangeStr(
        'Start ' +
          dayjs(start).format(
            startD.year() === new Date().getFullYear() ? 'MMM D' : 'MMM D, YYYY'
          )
      );
    } else if (end) {
      const endD = dayjs(end);
      setSelectedDateRangeStr(
        'Due ' +
          dayjs(end).format(
            endD.year() === new Date().getFullYear() ? 'MMM D' : 'MMM D, YYYY'
          )
      );
    } else {
      setSelectedDateRangeStr('Select Date');
    }
  }, [selectedDueDateRange]);

  // Combined validation schema for single Formik (name + description)
  const validationSchema = useMemo(
    () => editTaskNameSchema.concat(editTaskDescriptionSchema),
    []
  );

  const isProjectEditor =
    task?.currentUserProjectRole === ProjectMemberRole.Editor;

  const isTeamOwner = task?.currentUserProjectTeamRole
    ? task.currentUserProjectTeamRole === TeamMemberRole.Owner
    : false;

  const isTeamMember =
    task?.currentUserProjectTeamRole === TeamMemberRole.Member;

  const isTaskViewer =
    task?.currentUserRole === TaskMemberRole.Viewer &&
    task?.currentUserProjectRole !== ProjectMemberRole.Owner &&
    task?.currentUserProjectRole !== ProjectMemberRole.Admin &&
    task?.currentUserProjectRole !== ProjectMemberRole.Editor &&
    task?.currentUserProjectTeamRole !== TeamMemberRole.Owner &&
    task?.currentUserProjectTeamRole !== TeamMemberRole.Admin &&
    task?.currentUserProjectTeamRole !== TeamMemberRole.Member;

  const [_isProjectTaskFiles, setProjectTaskFiles] = useState<boolean>(true);

  const shouldEnableSelectMode = (
    isSelected: boolean,
    isViewer: boolean,
    isTeamMember: boolean
  ) => isSelected && (!isViewer || isTeamOwner || isTeamMember);

  const handelSelectedItem = (item: Document | LocalFile) => {
    const item0 = selectedDocuments?.find((item2) => {
      return item2.id == item.id;
    });
    if (item0) {
      const newArray = [...selectedDocuments].filter(
        (item3) => item3.id != item.id
      );
      setSelectedDocuments([...newArray]);
    } else if (selectedDocuments.length < 25) {
      setSelectedDocuments([...selectedDocuments, item]);
    } else {
      Alert.alert('Maximum 25 files can be selected at a time.');
    }
  };

  const { downloadFiles, isDownloading } = useDownloadFiles();
  const { processFiles } = useFileProcessor();
  const { launchFileSelection } = useFilePicker();
  const { me } = useMe();

  const [addTaskFiles] = useAddTaskFilesMutation({
    refetchQueries: [
      'listActivitiesByTask',
      'listTaskDocuments',
      { query: GetTaskDocument, variables: { id: task.id } },
    ],
  });

  const {
    uploadingFiles,
    setUploadingFiles,
    setUploadingErrorFiles,
    setUploadingSuccessFiles,
  } = useUploadFiles();

  const [isHoveredProject, setIsHoveredProject] = useState(false);
  const [isHoveredChecklist, setIsHoveredChecklist] = useState(false);
  const [isHoveredMembers, setIsHoveredMembers] = useState(false);
  const [isHoveredActivity, setIsHoveredActivity] = useState(false);
  const [isHoveredFilesAndMedia, setIsHoveredFilesAndMedia] = useState(false);
  const [isHoveredUploadFile, setIsHoveredUploadFile] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    Array<Document | LocalFile>
  >([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { deleteTaskFileById } = useDeleteTaskFiles();

  const { checkClientIds } = useGetDocumentsByClientIdsQuery();

  const { isConnected } = useNetInfo();
  const [isNetInfoModalVisible, setIsNetInfoModalVisible] = useState(false);

  const [_uploadProgress, setUploadProgress] = useState({});
  const { label } = getUploadProgressSummary(uploadingFiles);
  const callback = (
    documentClientId: string,
    uploadProgress: UploadProgressData
  ) => {
    const progress =
      uploadProgress.totalBytesSent / uploadProgress.totalBytesExpectedToSend;
    setUploadProgress({ ...uploadProgress, [documentClientId]: progress });

    setUploadingFiles((prev) =>
      prev.map((item) =>
        item.file.clientId === documentClientId ? { ...item, progress } : item
      )
    );
  };

  const getPhotosCheck = (imageList: LocalFile[]) => {
    if (shouldShowPaywallForProject(task?.project)) {
      setPaywallTeam(task?.project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    if (!isConnected) {
      setIsNetInfoModalVisible(true);
    } else {
      setIsNetInfoModalVisible(false);
      getPhotos(imageList);
    }
  };

  const getPhotos = (imageList: LocalFile[]) => {
    const documents = imageList.map((item, index): Document => {
      const {
        name,
        clientId,
        contentType,
        isImage = false,
        isAudio = false,
        duration = null,
      } = item;
      const documentId = `${task.id}-${index}`;

      return {
        __typename: 'Document',
        id: documentId,
        owner: me!,
        name: name || clientId,
        clientId,
        contentType,
        isImage,
        createdAt: new Date().toISOString(),
        size: item.size,
        file: {
          ...item,
          __typename: 'File',
          id: documentId,
          cdnBaseUrl: '',
          url: item.url, // this url is local file url to upload
        },
        isAudio,
        duration,
        progress: 0,
      } as any;
    });

    setUploadingFiles([...uploadingFiles, ...documents]);

    processFiles(documents, callback).then(
      (response) => {
        task.id &&
          addTaskFiles({
            variables: {
              id: task.id,
              attributes: imageList.map((item): DocumentAttributes => {
                const {
                  name,
                  clientId,
                  contentType,
                  isImage = false,
                  isAudio = false,
                } = item;
                const { blobId } =
                  response.find(
                    (uploadResultItem) => uploadResultItem.clientId === clientId
                  ) || {};

                return {
                  name,
                  contentType,
                  isImage,
                  isAudio,
                  blobId,
                  clientId,
                } as DocumentAttributes;
              }),
            },
            onCompleted: () => {
              checkClientIds(response.map((newItem) => newItem.clientId));
              setUploadingSuccessFiles([...documents]);
              setUploadingFiles([]);
            },
            onError: (e) => {
              if (
                e.message === t('models:projects.files.storageLimitReached')
              ) {
                Alert.alert(
                  'TaskTag',
                  t('models:projects.files.storageLimitReached')
                );
                setUploadingFiles([]);
                return;
              } else if (e.message) {
                Alert.alert('TaskTag', e.message);
                setUploadingFiles([]);
                return;
              }

              setUploadingErrorFiles([...documents]);
              setUploadingFiles([]);
            },
          });
      },
      () => {
        setUploadingErrorFiles([...documents]);
        setUploadingFiles([]);
      }
    );
  };

  const onDrop = async (files: File[]) => {
    const localFiles = await Promise.all(
      files.map((file) => fileToLocalFile(file))
    );

    const compressedLocalFiles = await compressImages(localFiles);
    getPhotosCheck(compressedLocalFiles);
  };

  const addFileOrMedia = () => {
    launchFileSelection().then((r) => {
      if (!r) return;

      const documents = r.map((item) => {
        return {
          ...item,
          path: '',
        };
      });
      getPhotosCheck(documents);
    });
  };

  const handleRetry = async (item: Document | LocalFile) => {
    if (item.__typename !== 'Document' || !task.id) return;

    const document = item as any;

    // Remove from error files
    setUploadingErrorFiles((prev) =>
      prev.filter((doc) => doc?.id !== document.id)
    );

    // Add to uploading files with progress 0
    setUploadingFiles((prev) => [...prev, document]);

    // Retry upload using processFiles and addProjectFiles
    try {
      const response = await processFiles([document], callback);

      await addTaskFiles({
        variables: {
          id: task.id,
          attributes: [
            {
              name: document.name,
              contentType: document.contentType || '',
              isImage: document.isImage || false,
              isAudio: document.isAudio || false,
              blobId:
                response.find((r) => r.clientId === document.clientId)
                  ?.blobId || '',
              clientId: document.clientId,
            } as DocumentAttributes,
          ],
        },
        onCompleted: () => {
          checkClientIds(response.map((newItem) => newItem.clientId));
          setUploadingSuccessFiles([document]);
          setUploadingFiles((prev) =>
            prev.filter((item) => item.file.id !== document.id)
          );
        },
        onError: (e) => {
          if (e.message === t('models:projects.files.storageLimitReached')) {
            Alert.alert(
              'TaskTag',
              t('models:projects.files.storageLimitReached')
            );
            setUploadingFiles([]);
            return;
          } else if (e.message) {
            Alert.alert('TaskTag', e.message);
            setUploadingFiles([]);
            return;
          }
          setUploadingErrorFiles((prev) => [...prev, document]);
          setUploadingFiles((prev) =>
            prev.filter((item) => item.file.id !== document.id)
          );
        },
      });
    } catch (error) {
      console.error('Retry upload failed:', error);
      setUploadingErrorFiles((prev) => [...prev, document]);
      setUploadingFiles((prev) =>
        prev.filter((item) => item.file.id !== document.id)
      );
    }
  };

  const selectedUsers = useMemo(() => {
    const list = task.members
      .filter((t) => t.role !== TaskMemberRole.Owner)
      .map((t) => {
        return {
          ...t.user,
          role: t.role,
          isTaskTagUser: false,
        } as UserSearchType;
      });

    const tasktagUserList =
      task?.userRequests?.map((t) => {
        return {
          ...t.user,
          role: t.role,
          isTaskTagUser: true,
        } as UserSearchType;
      }) ?? [];
    const emailList = (task.invitations ?? []).map(
      (t) =>
        ({
          title: t.email,
          role: t.role,
          expiredAt: t.expiredAt,
          id: t.id,
          __typename: 'StringSearchType',
        }) as StringSearchType
    );
    return [...list, ...tasktagUserList, ...emailList];
  }, [task.members, task.userRequests, task.invitations]);

  const taskPanelRef = useRef<HTMLDivElement>(null);

  const showInviteButton = useMemo(() => {
    return task?.invitations?.some(
      (item) =>
        item.expiredAt &&
        new Date(item.expiredAt) < new Date() &&
        item.email === me?.email
    );
  }, [task.invitations]);

  const showInvitePendingButton = useMemo(() => {
    return task?.userRequests?.some((item) => item.user?.email === me?.email);
  }, [task.userRequests]);

  const [showRequestSentCard, setShowRequestSentCard] = useState(
    showInvitePendingButton
  );

  useEffect(() => {
    if (showInvitePendingButton) {
      setShowRequestSentCard(true);
    }
  }, [showInvitePendingButton]);

  const [showJoinedGuidance, setShowJoinedGuidance] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const joinedId = window.sessionStorage.getItem('newly_joined_task_id');
    if (joinedId && joinedId === task.id) {
      if (showInvitePendingButton) return;
      setShowJoinedGuidance(true);
      window.sessionStorage.removeItem('newly_joined_task_id');
    }
  }, [task.id, showInvitePendingButton]);

  const taskOwner = useMemo(
    () => task?.members?.find((m) => m.role === TaskMemberRole.Owner),
    [task.members]
  );

  return (
    <>
      {paywallModalNode}
      <Box
        ref={taskPanelRef}
        position='relative'
        backgroundColor='white'
        flex={1}>
        {/* Header: project name, share link, more actions, close */}
        <Box
          px='l'
          py='m'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'>
          <Box flex={1} flexDirection='row' alignItems='center'>
            <TouchableOpacity
              disabled={
                showInviteButton ||
                showInvitePendingButton ||
                task?.currentUserRole === TaskMemberRole.Viewer
              }
              onPress={() => task.project && onPressProject?.(task.project)}
              style={{ flexShrink: 1 }}>
              <Box flex={1} flexDirection='row' alignItems='center'>
                <Box
                  justifyContent='center'
                  alignItems='center'
                  width={16}
                  height={16}>
                  <ProjectAvatar
                    icon={task?.project?.icon}
                    color={task?.project?.color}
                    variant='s'
                  />
                </Box>
                <Box
                  maxWidth={300}
                  onMouseEnter={() =>
                    !showInviteButton &&
                    !showInvitePendingButton &&
                    task?.currentUserRole !== TaskMemberRole.Viewer &&
                    setIsHoveredProject(true)
                  }
                  onMouseLeave={() =>
                    !showInviteButton &&
                    !showInvitePendingButton &&
                    task?.currentUserRole !== TaskMemberRole.Viewer &&
                    setIsHoveredProject(false)
                  }>
                  <Text
                    ref={textProjectNameRef}
                    flexShrink={1}
                    ml='xs'
                    ellipsizeMode='tail'
                    accessibilityLabel='Project Name'
                    variant='webBodyHintSubline'
                    color={
                      showInviteButton || showInvitePendingButton
                        ? 'grey04'
                        : 'textSecondary'
                    }
                    textDecorationLine={
                      isHoveredProject ? 'underline' : undefined
                    }
                    style={{ letterSpacing: 0.02 }}
                    numberOfLines={1}>
                    {task.project?.name || 'Unknown Project'}
                  </Text>
                </Box>
              </Box>
            </TouchableOpacity>
          </Box>
          <Box flex={1} alignItems='flex-end'>
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='center'>
              {!showInviteButton && !showInvitePendingButton && (
                <>
                  <Box
                    height={32}
                    width={32}
                    borderRadius='xs'
                    alignItems='center'
                    justifyContent='center'
                    mr='xxs'
                    pt='xxxs'
                    backgroundColor={
                      isHoveredCopyLinkToInvite ? 'grey01' : undefined
                    }
                    onMouseEnter={() => setIsHoveredCopyLinkToInvite(true)}
                    onMouseLeave={() => setIsHoveredCopyLinkToInvite(false)}
                    onPress={() => {
                      if (shouldShowPaywallForProject(task?.project)) {
                        setPaywallTeam(task?.project?.team ?? undefined);
                        setPaywallModalOpen(true);
                      }
                    }}>
                    <TaskShareLink
                      taskId={task.id}
                      project={task?.project}
                      canEdit={true}
                      hasTitle={false}
                      noMarginRight={true}
                      isTaskPanel
                    />
                  </Box>

                  {canViewTask && hasExecutableTaskOption && (
                    <PopupIcon
                      accessibilityLabel='Options'
                      name='MoreVertical'
                      variant='l'
                      color='textPrimary'
                      popupProps={{
                        position: 'left',
                        offset: [0, theme.spacing.m],
                        menuList: navItems,
                      }}
                    />
                  )}
                </>
              )}
              <Box
                borderRadius='xs'
                padding='xxs'
                ml='xxs'
                backgroundColor={isHoveredClose ? 'grey01' : undefined}
                onMouseEnter={() => setIsHoveredClose(true)}
                onMouseLeave={() => setIsHoveredClose(false)}>
                <Icon
                  accessibilityLabel='Close'
                  name='X'
                  variant='l'
                  onPress={onClose}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main content form: title, action bar, description */}
        {showInviteButton || showInvitePendingButton || !canViewTask ? (
          <Box
            px='l'
            py='m'
            borderTopColor='grey02'
            borderTopWidth={1}
            flex={1}>
            <Box p='xxs'>
              <Text
                accessibilityLabel='Task title'
                variant='labelLarge'
                numberOfLines={2}>
                {localTaskName}
              </Text>
            </Box>
            {!!localTaskDesc && (
              <Box backgroundColor='grey01' borderRadius='s' p='m' mt='m'>
                <Text variant='labelSmallBold' color='textPrimary' mb='xxs'>
                  Description
                </Text>
                <Text variant='webBodySecondary' color='textSecondary'>
                  {localTaskDesc}
                </Text>
              </Box>
            )}
            {showRequestSentCard ? (
              <Box mt='m'>
                <TaskRequestSentGuidanceCard
                  taskOwnerName={taskOwner?.user?.name}
                  taskOwnerId={taskOwner?.user?.id}
                  onNavigateToAccount={onNavigateToAccount}
                  onNavigateToProjects={onNavigateToProjects}
                />
              </Box>
            ) : (
              <Box flex={1} alignItems='center' justifyContent='center'>
                <TaskInviteRequestButton
                  currentUserHasPendingMemberAdditionRequest={
                    showInvitePendingButton
                  }
                  taskId={task?.id}
                  onRequestSent={() => setShowRequestSentCard(true)}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            px='l'
            py='m'
            borderTopColor='grey02'
            borderTopWidth={1}
            flex={1}>
            <Formik<FormValues>
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={() => {
                // We do not use a global submit button here.
                // All fields submit on blur or specific actions instead.
              }}
              validateOnMount>
              {({ values, isSubmitting, setFieldValue }) => {
                const priorityIcon = (disable: boolean) => (
                  <Box
                    alignItems='center'
                    justifyContent='center'
                    width={disable ? 32 : undefined}
                    height={disable ? 32 : undefined}
                    mr={disable ? 'xs' : undefined}
                    borderWidth={disable ? 1 : undefined}
                    borderRadius={disable ? 'm' : undefined}
                    borderColor={disable ? 'grey02' : undefined}>
                    {values.priority === TaskPriority.High && (
                      <Icon name='ChevronsUp' color='alertRed' variant='s' />
                    )}
                    {values.priority === TaskPriority.Low && (
                      <Icon name='ChevronsDown' color='blue' variant='s' />
                    )}
                    {values.priority === TaskPriority.Medium && (
                      <Icon name='Equals' color='pastelYellow' variant='s' />
                    )}
                  </Box>
                );
                // Priority button is built inside Formik so it can access form values
                const priorityButton =
                  canEditTaskSecondLevel || isProjectEditor || isTeamMember ? (
                    <BaseButtonV3
                      mr='xs'
                      modalRadius={8}
                      popModalComponent={({ onClose }) => (
                        <PrioritySelector
                          onPrioritySelect={(priority: TaskPriority) => {
                            // Update form value and send mutation
                            setFieldValue('priority', priority);
                            doUpdateTask(priority, values);
                            onClose();
                          }}
                        />
                      )}>
                      {priorityIcon(false)}
                    </BaseButtonV3>
                  ) : (
                    priorityIcon(true)
                  );

                const completeButton = (disable: boolean) => (
                  <Box
                    accessibilityLabel={
                      completed ? 'Mark Incomplete' : 'Mark Completed'
                    }
                    mr={disable ? 'xs' : undefined}
                    height={32}
                    width={159}
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='center'
                    backgroundColor={
                      // eslint-disable-next-line no-nested-ternary
                      completed
                        ? 'greenSecondary'
                        : isHoveredCompleteTask && !disable
                          ? 'grey01'
                          : 'white'
                    }
                    borderColor='greenSecondary'
                    borderRadius='m'
                    borderWidth={1}
                    onMouseEnter={() =>
                      !disable && setIsHoveredCompleteTask(true)
                    }
                    onMouseLeave={() =>
                      !disable && setIsHoveredCompleteTask(false)
                    }>
                    <Icon
                      marginRight='xs'
                      name='Check'
                      variant='s'
                      color={completed ? 'white' : 'greenSecondary'}
                    />
                    {completed ? (
                      <Text variant='labelSmall' color='white'>
                        Completed
                      </Text>
                    ) : (
                      <Text variant='labelSmall' color='greenSecondary'>
                        Mark Complete
                      </Text>
                    )}
                  </Box>
                );

                return (
                  <Box flex={1}>
                    {isEditing &&
                    (canEditTaskSecondLevel ||
                      isProjectEditor ||
                      isTeamMember) ? (
                      <Box p='xxs'>
                        <Field
                          component={TextFieldMultiline}
                          showCharsRemaining={true}
                          autoResizeHeight
                          multiline
                          label='Name'
                          name='name'
                          accessible
                          placeholder={t(
                            'models:tasks.placeholders.nameShortModal'
                          )}
                          borderless
                          autoFocus
                          isLargeText
                          marginBottom='none'
                          height={104}
                          ref={inputRef}
                          showScroll={false}
                          textInputProps={{
                            maxLength: TASK_NAME_LENGTH_LIMIT,
                            readOnly: isSubmitting,
                            fontFamily:
                              theme.textVariants.webBodySecondary.fontFamily,
                            fontsize:
                              theme.textVariants.webBodySecondary.fontSize,
                            lineHeight:
                              theme.textVariants.webBodySecondary.lineHeight,
                            color: theme.colors.textPrimary,
                          }}
                          accessibilityLabel={t(
                            'models:tasks.placeholders.nameShortModal'
                          )}
                          onKeyPress={(e: any) => {
                            isFilesAndMediaOpen && toggleFilesAndMediaSection();
                            isMembersOpen && toggleMembersSection();
                            isActivityOpen && toggleActivitySection();
                            if (e.nativeEvent.key === 'Escape') {
                              // Cancel editing and restore local name
                              setIsEditing(false);
                              setIsHovered(false);
                              setFieldValue('name', localTaskName);
                              setTimeout(() => {
                                inputRef.current?.blur();
                              }, 500);
                            }
                            if (e.nativeEvent.key === 'Enter' && !e.shiftKey) {
                              // Finish editing on Enter by blurring
                              inputRef.current?.blur();
                            }
                          }}
                          onBlur={() => {
                            setIsEditing(false);
                            setIsHovered(false);
                            // Submit only if name changed and not empty
                            if (
                              values.name !== localTaskName &&
                              values.name.trim()
                            ) {
                              submitTaskName(values);
                            } else {
                              // Restore previous value when nothing changed
                              setFieldValue('name', localTaskName);
                            }
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        p='xxs'
                        style={{
                          borderRadius: theme.spacing.xxs,
                          backgroundColor:
                            isHovered &&
                            (canEditTaskSecondLevel ||
                              isProjectEditor ||
                              isTeamMember)
                              ? theme.colors.grey01
                              : theme.colors.transparent,
                        }}
                        onMouseEnter={() =>
                          (canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember) &&
                          setIsHovered(true)
                        }
                        onMouseLeave={() =>
                          (canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember) &&
                          setIsHovered(false)
                        }>
                        <Text
                          accessibilityLabel='Task title'
                          variant='labelLarge'
                          numberOfLines={2}
                          onPress={
                            canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember
                              ? () => {
                                  if (
                                    status !== TaskStatus.Completed &&
                                    (canEditTaskSecondLevel ||
                                      isProjectEditor ||
                                      isTeamMember)
                                  ) {
                                    setIsEditing(true);
                                  }
                                }
                              : undefined
                          }>
                          {localTaskName}
                        </Text>
                      </Box>
                    )}

                    {/* Action bar: complete, priority, due date, assignees */}
                    <Box flexDirection='row' my='l'>
                      {canEditTaskSecondLevel ||
                      isProjectEditor ||
                      isTeamMember ? (
                        <BaseButtonV3
                          mr='xs'
                          onPress={() => {
                            if (completed) {
                              toggleCompleteTask(task);
                              return;
                            }
                            void ensureCanCompleteTask(task.id).then(
                              (canComplete) => {
                                if (canComplete) setShowCompleteTaskModal(true);
                              }
                            );
                          }}>
                          {completeButton(false)}
                        </BaseButtonV3>
                      ) : (
                        completeButton(true)
                      )}

                      {priorityButton}

                      <MyTasksDueDateRangePicker
                        accessibilityLabel='Date Selector'
                        disabled={
                          !(
                            canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember
                          )
                        }
                        selectedDuedate={selectedDueDateRange}
                        onSelectDuedate={
                          canEditTaskSecondLevel ||
                          isProjectEditor ||
                          isTeamMember
                            ? setSelectedDueDateRange
                            : undefined
                        }
                        title={selectedDateRangeStr}
                        iconName='Calendar'
                        boxProps={{
                          position: 'bottom',
                          offset: [0, 4],
                        }}
                        onDismiss={
                          !(
                            canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember
                          )
                            ? undefined
                            : () => {
                                // When picker closes and a date is selected, update task
                                if (
                                  selectedDueDateRange.start ||
                                  selectedDueDateRange.end
                                ) {
                                  updateTask({
                                    variables: {
                                      id: task.id,
                                      attributes: {
                                        ...values,
                                        startDate: selectedDueDateRange.start
                                          ? selectedDueDateRange.start.toISOString()
                                          : undefined,
                                        dueDate: selectedDueDateRange.end
                                          ? selectedDueDateRange.end.toISOString()
                                          : undefined,
                                      },
                                    },
                                  });
                                }
                              }
                        }
                      />
                      <Box marginLeft='xs' />
                      <TaskAssigneeMembersButtonV3
                        isProjectEditor={isProjectEditor}
                        isTeamMember={isTeamMember}
                        isTaskPanel
                        maxMemberCount={2}
                        doNotShrink
                        disabled={
                          !(
                            canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember
                          )
                        }
                        selectedUser={selectedUsers}
                        onAssignMember={(list: UserSearchType[]) => {
                          if (shouldShowPaywallForProject(task?.project)) {
                            setPaywallTeam(task?.project?.team ?? undefined);
                            setPaywallModalOpen(true);
                            return;
                          }
                          // Update form state and send mutation for members
                          setFieldValue(
                            'members',
                            list.map((m) => ({
                              userId: m.id,
                              role: m.role,
                            }))
                          );
                        }}
                        onSetShowInviteOrAssignModal={() => {}}
                        taskId={task.id}
                        project={task?.project}
                        initialOpen={openAssigneeModal}
                      />
                    </Box>

                    <Box flex={1}>
                      <ScrollView
                        showsVerticalScrollIndicator={false}
                        style={{
                          flex: 1,
                        }}
                        contentContainerStyle={{
                          paddingBottom: theme.spacing.l,
                        }}>
                        {showJoinedGuidance && (
                          <TaskJoinedGuidanceCard
                            onDismiss={() => setShowJoinedGuidance(false)}
                          />
                        )}
                        <Box backgroundColor='grey01' borderRadius='xs' p='m'>
                          <Text
                            variant='labelEmphasizedChatDetialTitle'
                            color='textSecondary'
                            mb='xs'>
                            Description
                          </Text>

                          {isEditingDes &&
                          (canEditTaskSecondLevel ||
                            isProjectEditor ||
                            isTeamMember) ? (
                            <Field
                              component={TextFieldMultiline}
                              label='Description'
                              accessibilityLabel='Description'
                              name='description'
                              placeholder={t(
                                'models:tasks.placeholders.description'
                              )}
                              showCharsRemaining
                              accessible
                              autoFocus
                              borderless
                              multiline
                              ref={inputRefDes}
                              height={60}
                              marginBottom='none'
                              showScroll={false}
                              autoResizeHeight
                              textInputProps={{
                                maxLength: TASK_DESC_LENGTH_LIMIT,
                                readOnly: isSubmitting,
                                textAlignVertical: 'top',
                                fontFamily:
                                  theme.textVariants.webBodySecondary
                                    .fontFamily,
                                fontsize:
                                  theme.textVariants.webBodySecondary.fontSize,
                                lineHeight:
                                  theme.textVariants.webBodySecondary
                                    .lineHeight,
                                color: theme.colors.textPrimary,
                              }}
                              onKeyPress={(e: any) => {
                                isFilesAndMediaOpen &&
                                  toggleFilesAndMediaSection();
                                isMembersOpen && toggleMembersSection();
                                isActivityOpen && toggleActivitySection();
                                if (e.nativeEvent.key === 'Escape') {
                                  // Cancel description editing
                                  setIsEditingDes(false);
                                  setFieldValue('description', localTaskDesc);
                                  setTimeout(() => {
                                    inputRefDes.current?.blur();
                                  }, 500);
                                }
                                if (
                                  e.nativeEvent.key === 'Enter' &&
                                  !e.shiftKey
                                ) {
                                  // Finish editing on Enter by blurring
                                  inputRefDes.current?.blur();
                                }
                              }}
                              onBlur={() => {
                                setIsEditingDes(false);
                                // Submit only when description changed
                                if (
                                  values.description !== localTaskDesc &&
                                  values.description.trim()
                                ) {
                                  submitTaskDescription(values);
                                } else {
                                  // Restore previous value when nothing changed
                                  setFieldValue('description', localTaskDesc);
                                  setIsHoveredDes(false);
                                }
                              }}
                            />
                          ) : (
                            <Box
                              style={{
                                backgroundColor:
                                  isHoveredDes &&
                                  (canEditTaskSecondLevel ||
                                    isProjectEditor ||
                                    isTeamMember)
                                    ? theme.colors.grey01
                                    : theme.colors.transparent,
                              }}
                              onMouseEnter={() =>
                                (canEditTaskSecondLevel ||
                                  isProjectEditor ||
                                  isTeamMember) &&
                                setIsHoveredDes(true)
                              }
                              onMouseLeave={() =>
                                (canEditTaskSecondLevel ||
                                  isProjectEditor ||
                                  isTeamMember) &&
                                setIsHoveredDes(false)
                              }>
                              <Text
                                accessibilityLabel='Task Description'
                                variant='webBodyHintSubline'
                                color={
                                  values.description &&
                                  values.description.length > 0
                                    ? 'textSecondary'
                                    : 'grey03'
                                }
                                onPress={
                                  canEditTaskSecondLevel ||
                                  isProjectEditor ||
                                  isTeamMember
                                    ? () => {
                                        if (
                                          status !== TaskStatus.Completed &&
                                          (canEditTaskSecondLevel ||
                                            isProjectEditor ||
                                            isTeamMember)
                                        ) {
                                          // Enable description editing and sync form value
                                          setIsEditingDes(true);
                                        }
                                      }
                                    : undefined
                                }>
                                {values.description &&
                                values.description.length > 0
                                  ? values.description
                                  : t('models:tasks.placeholders.description')}
                              </Text>
                            </Box>
                          )}
                        </Box>

                        {/* Checklist section with collapse / expand animation */}
                        <Box>
                          <Box
                            px='m'
                            mt='l'
                            mb='xs'
                            onMouseEnter={() => setIsHoveredChecklist(true)}
                            onMouseLeave={() => setIsHoveredChecklist(false)}
                            borderRadius='xs'
                            style={{
                              backgroundColor: isHoveredChecklist
                                ? theme.colors.grey01
                                : undefined,
                            }}>
                            <TouchableOpacity
                              onPress={toggleChecklistSection}
                              activeOpacity={0.7}>
                              <Box
                                py='m'
                                flexDirection='row'
                                alignItems='center'>
                                <Icon
                                  name='FileText'
                                  color='grey05'
                                  variant='m'
                                />
                                <Box
                                  flex={1}
                                  ml='xs'
                                  mr='l'
                                  flexDirection='row'
                                  alignItems='center'
                                  gap='xs'>
                                  <Text
                                    variant='labelEmphasized'
                                    color='textSecondary'>
                                    Checklist
                                  </Text>
                                  {(() => {
                                    const remainingCount = taskChecklist.filter(
                                      (item) => item.completedAt === null
                                    ).length;

                                    return (
                                      remainingCount > 0 && (
                                        <Box
                                          bg='grey01'
                                          borderRadius='xxs'
                                          px='xs'
                                          py='xxs'>
                                          <Text
                                            variant='webSmall'
                                            color='textSecondary'>
                                            {t(
                                              'models:tasks.checklist.checklistRemainingWithCount',
                                              {
                                                count: remainingCount,
                                              }
                                            )}
                                          </Text>
                                        </Box>
                                      )
                                    );
                                  })()}
                                </Box>
                                <Animated.View
                                  style={{
                                    transform: [{ rotate: checklistRotate }],
                                  }}>
                                  {/* Use ChevronDown and rotate it to look like Up/Down */}
                                  <Icon
                                    name='ChevronDown'
                                    color='grey05'
                                    variant='m'
                                  />
                                </Animated.View>
                              </Box>
                            </TouchableOpacity>
                          </Box>
                          <Box px='m'>
                            <Animated.View
                              style={{
                                height: checklistHeight,
                                overflow: 'hidden',
                              }}>
                              <Box
                                // Measure content height on layout so that the
                                // animation can interpolate from 0 -> content height
                                onLayout={(e) => {
                                  const h = e.nativeEvent.layout.height;
                                  const w = e.nativeEvent.layout.width;
                                  if (h !== checklistContentHeight) {
                                    setChecklistContentHeight(h);
                                  }
                                  if (w != checklistContentWidth) {
                                    setChecklistContentWidth(w);
                                  }
                                }}>
                                <Box
                                  flexDirection='row'
                                  mb='l'
                                  alignItems='center'>
                                  <Text
                                    flex={1}
                                    textAlign='right'
                                    mr='xs'
                                    variant='webSmall'
                                    color='textSecondary'>
                                    Show completed items
                                  </Text>
                                  <ToggleSwitch
                                    boxRadius={10}
                                    switchWidth={35}
                                    circleWidth={14}
                                    switchHeight={20}
                                    onValueChange={async (isEnabled) => {
                                      setShowChecklistCompletedItems(isEnabled);
                                    }}
                                    value={showChecklistCompletedItems}
                                  />
                                </Box>

                                {checklistLoading &&
                                taskChecklist.length === 0 ? (
                                  <SkeletonList
                                    row={(index) => (
                                      <ChecklistRowSkeleton key={index} />
                                    )}
                                  />
                                ) : (
                                  <>
                                    <DndKitSortableChecklistListWeb
                                      canEditTaskSecondLevel={
                                        canEditTaskSecondLevel ||
                                        isProjectEditor ||
                                        isTeamMember
                                      }
                                      width={
                                        checklistContentWidth > 500
                                          ? checklistContentWidth
                                          : 500
                                      }
                                      listTaskChecklist={taskChecklist}
                                      showChecklistCompletedItems={
                                        showChecklistCompletedItems
                                      }
                                      onCompletedTaskChecklistItem={
                                        onCompletedTaskChecklistItem
                                      }
                                      onDeleteTaskChecklistItem={
                                        onDeleteTaskChecklistItem
                                      }
                                      onSaveTaskChecklistItem={
                                        onSaveTaskChecklistItem
                                      }
                                      onReorder={onDragEnd}
                                    />

                                    {processing && (
                                      <Box
                                        flexDirection='row'
                                        alignItems='center'
                                        justifyContent='center'
                                        mb='l'>
                                        <ActivityIndicator
                                          color={theme.colors.textPrimary}
                                          size={16}
                                        />
                                      </Box>
                                    )}

                                    {(canEditTaskSecondLevel ||
                                      isProjectEditor ||
                                      isTeamMember) && (
                                      <TaskChecklistItem
                                        canEditTaskSecondLevel={
                                          canEditTaskSecondLevel ||
                                          isProjectEditor ||
                                          isTeamMember
                                        }
                                        onCompletedTaskChecklistItem={
                                          onCompletedTaskChecklistItem
                                        }
                                        onDeleteTaskChecklistItem={
                                          onDeleteTaskChecklistItem
                                        }
                                        onSaveTaskChecklistItem={
                                          onSaveTaskChecklistItem
                                        }
                                      />
                                    )}
                                  </>
                                )}
                              </Box>
                            </Animated.View>
                          </Box>
                        </Box>

                        {/* Files & Meida section with collapse / expand animation */}
                        <Box>
                          <Box
                            px='m'
                            mt='l'
                            onMouseEnter={() => setIsHoveredFilesAndMedia(true)}
                            onMouseLeave={() =>
                              setIsHoveredFilesAndMedia(false)
                            }
                            borderRadius='xs'
                            style={{
                              backgroundColor: isHoveredFilesAndMedia
                                ? theme.colors.grey01
                                : undefined,
                            }}>
                            <TouchableOpacity
                              onPress={toggleFilesAndMediaSection}
                              activeOpacity={0.7}>
                              <Box
                                py='xs'
                                my='xxxs'
                                flexDirection='row'
                                alignItems='center'>
                                <Icon name='Image' color='grey05' variant='m' />
                                <Text
                                  flex={1}
                                  ml='xs'
                                  mr='l'
                                  variant='labelEmphasized'
                                  color='textSecondary'>
                                  Files & Media
                                </Text>
                                {uploadingFiles.length > 0 && (
                                  <Box
                                    flexDirection='row'
                                    mr='xs'
                                    alignItems='center'
                                    justifyContent='center'>
                                    <ActivityIndicator
                                      color={theme.colors.textPrimary}
                                      size={16}
                                    />
                                    <Text
                                      variant='webBodySecondary'
                                      color='textSecondary'
                                      style={{ marginLeft: 6 }}>
                                      {label}
                                    </Text>
                                  </Box>
                                )}
                                {(canEditTaskSecondLevel ||
                                  isTeamMember ||
                                  isProjectEditor) && (
                                  <Box marginRight='m'>
                                    <TooltipWebV3
                                      marginOffset={0}
                                      title={
                                        <Text
                                          numberOfLines={0}
                                          maxWidth={300}
                                          flexShrink={1}
                                          variant='labelSmall'
                                          color='white'>
                                          Upload File
                                        </Text>
                                      }
                                      placement='bottom-end'>
                                      <TouchableOpacity
                                        accessibilityLabel='Upload File'
                                        onPress={addFileOrMedia}
                                        activeOpacity={0.7}>
                                        <Box
                                          p='xs'
                                          onMouseEnter={() =>
                                            setIsHoveredUploadFile(true)
                                          }
                                          onMouseLeave={() =>
                                            setIsHoveredUploadFile(false)
                                          }
                                          borderRadius='xs'
                                          style={{
                                            backgroundColor: isHoveredUploadFile
                                              ? theme.colors.grey02
                                              : undefined,
                                          }}>
                                          <Icon
                                            name='ImageUpload'
                                            color='grey05'
                                            variant='m'
                                            onPress={addFileOrMedia}
                                          />
                                        </Box>
                                      </TouchableOpacity>
                                    </TooltipWebV3>
                                  </Box>
                                )}
                                <Animated.View
                                  style={{
                                    transform: [
                                      { rotate: filesAndMediaRotate },
                                    ],
                                  }}>
                                  {/* Use ChevronDown and rotate it to look like Up/Down */}
                                  <Icon
                                    name='ChevronDown'
                                    color='grey05'
                                    variant='m'
                                  />
                                </Animated.View>
                              </Box>
                            </TouchableOpacity>
                          </Box>
                          <Box px='m'>
                            <Animated.View
                              style={{
                                height: filesAndMediaHeight,
                                overflow: 'hidden',
                              }}>
                              <Box
                                // Measure content height on layout so that the
                                // animation can interpolate from 0 -> content height
                                onLayout={(e) => {
                                  const h = e.nativeEvent.layout.height;
                                  if (
                                    h !== filesAndMediaContentHeight &&
                                    !isShowFilter
                                  ) {
                                    setFilesAndMediaContentHeight(h);
                                  }
                                }}>
                                {(canEditTaskSecondLevel ||
                                  isTeamMember ||
                                  isProjectEditor) && (
                                  <TaskPanelDragOverlay
                                    containerRef={taskPanelRef}
                                    onFiles={(files) => {
                                      onDrop(files);
                                    }}
                                  />
                                )}
                                {isFilesAndMediaOpen ? (
                                  <TaskFilesAndMedia
                                    taskId={task.id}
                                    taskMembers={task.members}
                                    isSelectMode={isSelectMode}
                                    setIsSelectMode={setIsSelectMode}
                                    setIsShowFilter={setIsShowFilter}
                                    onSelectedModeChange={(
                                      isSelected: boolean
                                    ) => {
                                      setIsSelectMode(
                                        shouldEnableSelectMode(
                                          isSelected,
                                          isTaskViewer,
                                          isTeamMember
                                        )
                                      );
                                      setProjectTaskFiles(true);
                                    }}
                                    selectedList={selectedDocuments}
                                    onChangeSelectedList={setSelectedDocuments}
                                    handelSelectedItem={handelSelectedItem}
                                    addFileOrMedia={addFileOrMedia}
                                    onRetry={handleRetry}
                                    isNotViewer={
                                      canEditTaskSecondLevel ||
                                      isTeamMember ||
                                      isProjectEditor
                                    }
                                  />
                                ) : null}
                              </Box>
                            </Animated.View>
                          </Box>
                        </Box>

                        {/* Members section with collapse / expand animation */}
                        <Box>
                          <Box
                            px='m'
                            mt='l'
                            onMouseEnter={() => setIsHoveredMembers(true)}
                            onMouseLeave={() => setIsHoveredMembers(false)}
                            borderRadius='xs'
                            style={{
                              backgroundColor: isHoveredMembers
                                ? theme.colors.grey01
                                : undefined,
                            }}>
                            <TouchableOpacity
                              onPress={toggleMembersSection}
                              activeOpacity={0.7}>
                              <Box
                                py='m'
                                flexDirection='row'
                                alignItems='center'>
                                <Icon name='Users' color='grey05' variant='m' />
                                <Text
                                  flex={1}
                                  ml='xs'
                                  mr='l'
                                  variant='labelEmphasized'
                                  color='textSecondary'>
                                  Assignee
                                </Text>
                                <Animated.View
                                  style={{
                                    transform: [{ rotate: membersRotate }],
                                  }}>
                                  {/* Use ChevronDown and rotate it to look like Up/Down */}
                                  <Icon
                                    name='ChevronDown'
                                    color='grey05'
                                    variant='m'
                                  />
                                </Animated.View>
                              </Box>
                            </TouchableOpacity>
                          </Box>
                          <Box px='m'>
                            <Animated.View
                              style={{
                                height: membersHeight,
                                overflow: 'hidden',
                              }}>
                              <Box
                                // Measure content height on layout so that the
                                // animation can interpolate from 0 -> content height
                                onLayout={(e) => {
                                  const h = e.nativeEvent.layout.height;
                                  if (h !== membersContentHeight) {
                                    setMembersContentHeight(h);
                                  }
                                }}>
                                {isMembersOpen ? (
                                  <TaskMembersNew
                                    canEditTaskSecondLevel={
                                      canEditTaskSecondLevel ||
                                      isProjectEditor ||
                                      isTeamMember
                                    }
                                    taskId={task.id}
                                  />
                                ) : null}
                              </Box>
                            </Animated.View>
                          </Box>
                        </Box>

                        {/* Activity section with collapse / expand animation */}
                        <Box>
                          <Box
                            px='m'
                            mt='l'
                            onMouseEnter={() => setIsHoveredActivity(true)}
                            onMouseLeave={() => setIsHoveredActivity(false)}
                            borderRadius='xs'
                            style={{
                              backgroundColor: isHoveredActivity
                                ? theme.colors.grey01
                                : undefined,
                            }}>
                            <TouchableOpacity
                              onPress={toggleActivitySection}
                              activeOpacity={0.7}
                              accessibilityRole='button'
                              accessibilityLabel={
                                (task.unreadAuditsCount ?? 0) > 0
                                  ? 'Activity, unread'
                                  : 'Activity'
                              }>
                              <Box
                                py='m'
                                flexDirection='row'
                                alignItems='center'>
                                <Icon
                                  name='Activity1'
                                  color='grey05'
                                  variant='m'
                                />
                                <Box
                                  flex={1}
                                  flexDirection='row'
                                  alignItems='center'
                                  ml='xs'
                                  mr='l'>
                                  <Text
                                    variant='labelEmphasized'
                                    color='textSecondary'>
                                    Activity
                                  </Text>
                                  {(task.unreadAuditsCount ?? 0) > 0 && (
                                    <Box
                                      testID='task-activity-unread-dot'
                                      marginStart='xxs'
                                      width={theme.spacing.xs}
                                      height={theme.spacing.xs}
                                      backgroundColor='greenSecondary'
                                      borderRadius='xs'
                                    />
                                  )}
                                </Box>
                                <Animated.View
                                  style={{
                                    transform: [{ rotate: activityRotate }],
                                  }}>
                                  {/* Use ChevronDown and rotate it to look like Up/Down */}
                                  <Icon
                                    name='ChevronDown'
                                    color='grey05'
                                    variant='m'
                                  />
                                </Animated.View>
                              </Box>
                            </TouchableOpacity>
                          </Box>
                          <Box>
                            <Animated.View
                              style={{
                                height: activityHeight,
                                overflow: 'hidden',
                              }}>
                              <Box
                                // Measure content height on layout so that the
                                // animation can interpolate from 0 -> content height
                                onLayout={(e) => {
                                  const h = e.nativeEvent.layout.height;
                                  if (h !== activityContentHeight) {
                                    setActivityContentHeight(h);
                                  }
                                }}>
                                {isActivityOpen ? (
                                  <TaskActivityLog
                                    externalNavigate={externalNavigate}
                                    taskID={task.id}
                                    noMargin
                                  />
                                ) : null}
                              </Box>
                            </Animated.View>
                          </Box>
                        </Box>
                      </ScrollView>
                    </Box>
                  </Box>
                );
              }}
            </Formik>
          </Box>
        )}
      </Box>

      {/* Delete task confirmation modal — TA-10949 #13/#14: shares
          ChecklistCommonModal and its copy with the list's right-click
          delete (MyTaskList.web.tsx) so both entry points match. This
          sidebar entry point only ever deletes the one open task, so
          skippedItems stays empty (no bullet list, per design). */}
      {showDeleteTaskModal && (
        <ChecklistCommonModal
          title={t('models:deleteTask.confirmTitle')}
          message={t('models:deleteTask.confirmMessage', { count: 1 })}
          buttonTitle={t('models:deleteTask.confirmButton')}
          visible={showDeleteTaskModal}
          skippedItems={[]}
          isLoading={isDeletingTask}
          onPressDelete={() => {
            deleteTask({
              variables: {
                id: task.id,
              },
            });
          }}
          messageEnd={t('models:deleteTask.confirmMessageEnd', { count: 1 })}
          onClose={() => setShowDeleteTaskModal(false)}
        />
      )}

      {/* Complete task confirmation modal */}
      {showCompleteTaskModal && (
        <ConfirmModal
          showModal={showCompleteTaskModal}
          onClose={() => setShowCompleteTaskModal(false)}
          onPress={() => {
            toggleCompleteTask(task);
            setShowCompleteTaskModal(false);
          }}
          buttonText={t('models:tasks.complete.title')}
          title={t('models:tasks.complete.completeTask')}
          message={t('models:tasks.complete.completeTaskMessage')}
          isAlert
        />
      )}
      {isNetInfoModalVisible && (
        <NetInfoModal
          onClose={() => setIsNetInfoModalVisible(false)}
          showModal={isNetInfoModalVisible}
          title={t('models:chat.netInfoTitle')}
          message={t('models:chat.netInfoMessage')}
          buttonTextOne={t('models:onBoarding.error.okPress')}
        />
      )}
      {showDeleteModal && (
        <ConfirmModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onPress={() => {
            setShowDeleteModal(false);
            const taskDocs = selectedDocuments?.flatMap(
              (item) => item.taskDocuments
            );
            const taskDoc = taskDocs?.filter((i) => i.task?.id === task.id);
            const messagedoc = selectedDocuments.map(
              (item) => item?.messageDocuments
            );
            const chatId =
              messagedoc[0]?.map((item) => item.message?.chatId) || '';

            deleteTaskFileById(taskDoc, chatId[0], () => {
              toast.show(
                <FileDeletedToast
                  title={
                    selectedDocuments.length > 1
                      ? t('models:files.filesDeleted')
                      : t('models:files.fileDeleted')
                  }
                  duration={3000}
                />,
                {
                  type: 'custom',
                  duration: 3000,
                  placement: 'bottom',
                  animationType: 'slide-in',
                  style: {
                    backgroundColor: 'transparent',
                    shadowColor: 'transparent',
                    padding: 0,
                    margin: 0,
                    width: 240,
                    height: 80,
                  },
                }
              );
            });
            setSelectedDocuments([]);
            setIsSelectMode(false);
          }}
          buttonText={t('models:deleteTask.title')}
          title={
            selectedDocuments.length > 1
              ? t('models:files.deleteFiles')
              : t('models:files.deleteFile')
          }
          message={t('models:files.deleteItemsWithCount', {
            count: selectedDocuments.length,
          })}
        />
      )}
      {isSelectMode && (
        <Box
          backgroundColor='white'
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
          }}>
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='center'
            height={50}
            py='m'
            px='l'
            style={{
              backgroundColor: '#1F2937',
            }}>
            <TooltipWebV3
              marginOffset={0}
              variant='dark'
              minWidth='auto'
              title={
                <Text
                  numberOfLines={0}
                  maxWidth={300}
                  flexShrink={1}
                  variant='labelSmall'
                  color='white'>
                  Clear selection
                </Text>
              }
              placement='top'>
              <Icon
                name='XCircleNew'
                color='white'
                variant='l'
                marginRight='xs'
                onPress={() => {
                  setSelectedDocuments([]);
                  setIsSelectMode(false);
                }}
              />
            </TooltipWebV3>
            <Text variant='body' color='white'>
              {t('shared:itemsSelectedWithCount', {
                count: selectedDocuments.length,
              })}
            </Text>

            <Box flex={1} />

            {(canEditTaskSecondLevel || isProjectEditor || isTeamMember) && (
              <TooltipWebV3
                marginOffset={0}
                variant='dark'
                minWidth='auto'
                title={
                  <Text
                    numberOfLines={0}
                    maxWidth={300}
                    flexShrink={1}
                    variant='labelSmall'
                    color='white'>
                    Delete File
                  </Text>
                }
                placement='top'>
                <Icon
                  name='Trash2'
                  color='white'
                  variant='l'
                  marginRight='l'
                  onPress={() => {
                    setShowDeleteModal(true);
                  }}
                />
              </TooltipWebV3>
            )}

            {isDownloading ? (
              <Box
                alignItems='center'
                justifyContent='center'
                marginRight='l'
                style={{ width: 24, height: 24 }}>
                <ActivityIndicator color='white' />
              </Box>
            ) : (
              <TooltipWebV3
                marginOffset={0}
                variant='dark'
                minWidth='auto'
                title={
                  <Text
                    numberOfLines={0}
                    maxWidth={300}
                    flexShrink={1}
                    variant='labelSmall'
                    color='white'>
                    Download
                  </Text>
                }
                placement='top'>
                <Icon
                  name='Download'
                  color='white'
                  variant='l'
                  marginRight='l'
                  onPress={() => {
                    downloadFiles(selectedDocuments, false);
                  }}
                />
              </TooltipWebV3>
            )}

            {task?.currentUserRole !== TaskMemberRole.Viewer && (
              <Box marginTop='xxs'>
                {shouldShowPaywallForProject(task?.project) ? (
                  <UpgradePaywallTooltip show={true}>
                    <Box>
                      <WebIcon
                        isWhiteIcon
                        accessibilityLabel='Copy Link to Invite'
                        name='Link'
                        color='grey04'
                        variant='s'
                        onPress={() => {
                          setPaywallTeam(task?.project?.team ?? undefined);
                          setPaywallModalOpen(true);
                        }}
                      />
                    </Box>
                  </UpgradePaywallTooltip>
                ) : (
                  <FileShareLink selectedDocuments={selectedDocuments} />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
